import React, { useState, useEffect } from 'react';

/**
 * USOKIE Platform Sidebar
 * Matching the landing page design: Black (#000), Bone (#fffbf7), Sage (#a1c6a2)
 */

const Sidebar = () => {
  const [user, setUser] = useState({ name: '', initials: 'U' });

  useEffect(() => {
    // Fetch user profile data
    const fetchUserProfile = async () => {
      try {
        const response = await fetch('/api/v1/user_profiles/me', {
          headers: { 'Accept': 'application/json' },
          credentials: 'include'
        });
        if (response.ok) {
          const data = await response.json();
          const name = data.name || 'User';
          const initials = name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'U';
          setUser({ name, initials });
        }
      } catch (error) {
        console.log('Failed to fetch user profile');
      }
    };
    fetchUserProfile();
  }, []);

  return (
    <aside className="w-64 flex-shrink-0 bg-black border-r border-[rgba(255,251,247,0.1)] flex flex-col">
      {/* Brand */}
      <div className="p-6 border-b border-[rgba(255,251,247,0.1)]">
        <a href="/" data-turbo="false" className="flex items-center gap-4">
          {/* USOKIE Brand Mark - Chain-link Symbol */}
          <img
            src="/images/usokie-symbol-sage.png"
            alt="USOKIE"
            className="w-10 h-10"
          />
          <div className="flex flex-col">
            <span className="font-bold text-[#fffbf7] text-sm tracking-[0.1em]">
              USOKIE
            </span>
            <span className="text-[#a1c6a2] text-[10px] tracking-[0.2em] uppercase" style={{ fontFamily: "'GT Pressura Mono', monospace" }}>
              Platform
            </span>
          </div>
        </a>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-6">
        {/* Primary Navigation */}
        <div className="px-6 mb-4">
          <span
            className="text-[10px] tracking-[0.3em] uppercase text-[#a1c6a2]"
            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
          >
            Navigation
          </span>
        </div>

        <NavItem label="Home" href="/" />

        {/* Divider */}
        <div className="my-6 mx-6 h-px bg-[rgba(255,251,247,0.1)]" />

        <div className="px-6 mb-4">
          <span
            className="text-[10px] tracking-[0.3em] uppercase text-[#a1c6a2]"
            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
          >
            Lifecycle
          </span>
        </div>

        <NavItem label="Intake & Alignment" href="/deal_desk" number="1" />
        <NavItem label="Contracts" href="/contracts" number="2" />
        <NavItem label="Design" href="/design" number="3" />
        <NavItem label="SKUs" href="/skus" number="4" />
        <NavItem label="Manufacturing" href="/manufacturing" number="5" />
        <NavItem label="In Market" href="/in-market" number="6" />
        <NavItem label="Reporting" href="/reporting" number="7" />

        {/* Divider */}
        <div className="my-6 mx-6 h-px bg-[rgba(255,251,247,0.1)]" />

        <div className="px-6 mb-4">
          <span
            className="text-[10px] tracking-[0.3em] uppercase text-[#a1c6a2]"
            style={{ fontFamily: "'GT Pressura Mono', monospace" }}
          >
            Registry
          </span>
        </div>

        <NavItem label="IP Assets" href="/ip_objects" />
        <NavItem label="Parties" href="/parties" />
      </nav>

      {/* User Profile */}
      <div className="border-t border-[rgba(255,251,247,0.1)]">
        <div className="p-4">
          <a
            href="/profile/edit"
            data-turbo="false"
            className="flex items-center gap-3 p-3 border border-[rgba(255,251,247,0.2)] hover:border-[#a1c6a2] transition-colors cursor-pointer"
          >
            {/* User Initials */}
            <div className="w-8 h-8 bg-[rgba(255,251,247,0.1)] flex items-center justify-center">
              <span className="text-[#fffbf7] text-xs font-bold">{user.initials}</span>
            </div>
            <div className="flex-1 text-left">
              <p className="text-[#fffbf7] text-xs font-medium">
                {user.name || 'User'}
              </p>
              <p
                className="text-[rgba(255,251,247,0.5)] text-[10px] tracking-[0.1em] uppercase"
                style={{ fontFamily: "'GT Pressura Mono', monospace" }}
              >
                Edit Profile
              </p>
            </div>
            {/* Edit Icon */}
            <svg className="w-4 h-4 text-[rgba(255,251,247,0.4)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
            </svg>
          </a>
        </div>

        {/* Logout */}
        <button
          onClick={() => {
            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content;
            fetch('/auth/logout', {
              method: 'DELETE',
              headers: { 'X-CSRF-Token': csrfToken }
            }).then(() => window.location.href = '/');
          }}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 border-t border-[rgba(255,251,247,0.1)] text-[rgba(255,251,247,0.5)] hover:bg-[rgba(255,251,247,0.05)] hover:text-[#fffbf7] transition-all text-xs tracking-[0.15em] uppercase"
          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
        >
          <span>Sign Out</span>
        </button>
      </div>
    </aside>
  );
};

/**
 * Navigation Item
 */
const NavItem = ({ label, href, number }) => {
  const isActive = href === '/'
    ? window.location.pathname === '/'
    : window.location.pathname === href || window.location.pathname.startsWith(href + '/');

  return (
    <a
      href={href}
      data-turbo="false"
      className={`
        w-full flex items-center px-6 py-3 transition-all border-l-2
        ${isActive
          ? 'bg-[rgba(161,198,162,0.05)] border-l-[#a1c6a2] text-[#a1c6a2]'
          : 'border-l-transparent text-[rgba(255,251,247,0.6)] hover:bg-[rgba(255,251,247,0.05)] hover:text-[#fffbf7]'}
      `}
    >
      {number && (
        <span
          className="w-5 h-5 flex items-center justify-center mr-3 text-[10px] border border-current opacity-50"
          style={{ fontFamily: "'GT Pressura Mono', monospace" }}
        >
          {number}
        </span>
      )}
      <span className="text-sm">
        {label}
      </span>
    </a>
  );
};

export default Sidebar;
